import { useT } from '@transifex/react';
import { cn, Text, VStack } from '@vidahealth/vida-ui';
import LottieView from 'lottie-react-native';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import Svg from 'react-native-svg';

import scanLottie from '@/assets/lotties/scan-lottie.json';

import { AnimatedCircle } from '../AnimatedCircle';
import { Screen } from '../Screen';

// Full-screen "checking eligibility" state, shown while a real-time (RTE)
// eligibility check is in flight. The scanLottie sits inside a progress ring,
// mirroring stylos's HoldTightContent two-timer flow:
//   Phase 1 (countdown): the ring fills over ~10s + the first message shows.
//   Phase 2 (indefinite): once it fills, the ring spins forever + second message.
const COUNTDOWN_MS = 10_000;

// Ring geometry. Size + colors mirror stylos's LottieProgress defaults.
const RING_SIZE = 150;
const STROKE = 6;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const ARC = CIRCUMFERENCE * 0.25; // visible sweep of the spinning (phase 2) arc
// The lottie is clipped to the ring's inner diameter so the scan art (whose
// cards extend to the artboard edges) never bleeds past the ring stroke.
const CLIP_SIZE = RING_SIZE - STROKE * 2;

// Geometry shared by every ring circle (only the stroke color / dash differ).
const RING_CIRCLE = {
  cx: RING_SIZE / 2,
  cy: RING_SIZE / 2,
  r: RADIUS,
  strokeWidth: STROKE,
  fill: 'none',
} as const;

// Track (faint backdrop) vs. progress (the blue sweep) stroke tokens.
const TRACK_CLASS = 'stroke-primary-50 dark:stroke-primary-900';
const PROGRESS_CLASS = 'stroke-primary-500';

export function HoldTightScreen({ className }: { className?: string }) {
  const t = useT();
  const [stage, setStage] = useState<0 | 1>(0);
  const progress = useSharedValue(0); // 0→1 determinate fill (phase 1)
  const rotation = useSharedValue(0); // continuous spin (phase 2)

  const fillProps = useAnimatedProps(() => ({
    strokeDashoffset: CIRCUMFERENCE * (1 - progress.value),
  }));
  const spinStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  useEffect(() => {
    // Phase 1: fill the ring over the countdown, then advance to phase 2.
    progress.value = withTiming(1, { duration: COUNTDOWN_MS, easing: Easing.linear });
    const id = setTimeout(() => setStage(1), COUNTDOWN_MS);
    return () => clearTimeout(id);
  }, [progress]);

  useEffect(() => {
    // Phase 2: spin indefinitely once the countdown has elapsed. Cancel on
    // unmount so the infinite repeat stops explicitly — symmetric with phase 1's
    // clearTimeout above, rather than relying on Reanimated to GC the value.
    if (stage === 1) {
      rotation.value = withRepeat(withTiming(360, { duration: 1200, easing: Easing.linear }), -1);
    }
    return () => cancelAnimation(rotation);
  }, [stage, rotation]);

  const title = stage === 0 ? t('Hold Tight') : t('Taking longer than usual');
  const subtitle =
    stage === 0
      ? t("We're checking with your insurance company...")
      : t('Please stay on this screen. We are checking other sources...');

  return (
    <Screen
      className={cn('bg-background-0 dark:bg-neutral-950', className)}
      contentStyle={styles.content}
    >
      <View style={styles.ringWrap}>
        {stage === 0 ? (
          // Determinate countdown: arc grows from the top (Svg rotated -90°).
          <Svg
            width={RING_SIZE}
            height={RING_SIZE}
            style={[StyleSheet.absoluteFill, styles.fromTop]}
          >
            <AnimatedCircle {...RING_CIRCLE} className={TRACK_CLASS} />
            <AnimatedCircle
              {...RING_CIRCLE}
              className={PROGRESS_CLASS}
              strokeLinecap='round'
              strokeDasharray={CIRCUMFERENCE}
              animatedProps={fillProps}
            />
          </Svg>
        ) : (
          // Indefinite spin: a fixed arc rotates continuously.
          <Animated.View style={[StyleSheet.absoluteFill, spinStyle]}>
            <Svg width={RING_SIZE} height={RING_SIZE}>
              <AnimatedCircle {...RING_CIRCLE} className={TRACK_CLASS} />
              <AnimatedCircle
                {...RING_CIRCLE}
                className={PROGRESS_CLASS}
                strokeLinecap='round'
                strokeDasharray={`${ARC} ${CIRCUMFERENCE}`}
              />
            </Svg>
          </Animated.View>
        )}
        <View style={styles.lottieClip}>
          <LottieView
            source={scanLottie}
            autoPlay
            loop
            style={{ width: CLIP_SIZE, height: CLIP_SIZE }}
            webStyle={{ width: CLIP_SIZE, height: CLIP_SIZE }}
          />
        </View>
      </View>

      <VStack space='sm' className='items-center'>
        <Text size='xl' className='text-center font-heading-semibold text-typography-900'>
          {title}
        </Text>
        <Text className='text-center text-typography-700'>{subtitle}</Text>
      </VStack>
    </Screen>
  );
}

const styles = StyleSheet.create({
  content: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 32,
  },
  ringWrap: {
    width: RING_SIZE,
    height: RING_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Rotate the determinate ring so its fill begins at 12 o'clock.
  fromTop: {
    transform: [{ rotate: '-90deg' }],
  },
  lottieClip: {
    width: CLIP_SIZE,
    height: CLIP_SIZE,
    borderRadius: CLIP_SIZE / 2,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
